import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Users, Trash2, Edit, X, Save, Loader2, GitBranch, Sparkles, RefreshCw, Database, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { ProviderLogo } from "@/components/ProviderLogo";
import { ProblemTypeEditor } from "@/components/ProblemTypeEditor";

const DEFAULT_PROBLEM_TYPES = [
  {
    key: "scheduled_job",
    label: "Scheduled job failed",
    flow: ["Check IAM policy", "Inspect SQS DLQ", "Replay job"],
    identifiers: [
      { key: "job_id", label: "Job ID", regex: "JOB-\\d+" },
    ],
    flow_steps: [
      { id: "s1", step_type: "cloudwatch", label: "Last 24h errors", config: { log_group: "/aws/lambda/scheduleJob-prod", query: "filter @message like /AccessDenied/", time_window: "last_24h" } },
    ],
  },
];

export default function Squads() {
  const { lang } = useLang();
  const [squads, setSquads] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", integration_ids: [], problem_types: DEFAULT_PROBLEM_TYPES });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [s, i] = await Promise.all([api.get("/squads"), api.get("/integrations")]);
    setSquads(s.data.squads || []);
    setIntegrations(i.data.integrations || []);
  };

  useEffect(() => { load(); }, []);

  const reset = () => {
    setForm({ name: "", description: "", integration_ids: [], problem_types: DEFAULT_PROBLEM_TYPES });
    setEditing(null);
    setShowNew(false);
  };

  const startEdit = (sq) => {
    setForm({
      name: sq.name,
      description: sq.description || "",
      integration_ids: sq.integration_ids || [],
      problem_types: sq.problem_types?.length ? sq.problem_types : DEFAULT_PROBLEM_TYPES,
    });
    setEditing(sq.id);
    setShowNew(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error(lang === "pt" ? "Nome obrigatório" : "Name required");
    setSaving(true);
    try {
      if (editing) await api.put(`/squads/${editing}`, form);
      else await api.post("/squads", form);
      toast.success(lang === "pt" ? "Squad salvo" : "Squad saved");
      reset();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm(lang === "pt" ? "Remover este squad?" : "Delete this squad?")) return;
    await api.delete(`/squads/${id}`);
    load();
  };

  const toggleInteg = (id) => {
    const has = form.integration_ids.includes(id);
    setForm({ ...form, integration_ids: has ? form.integration_ids.filter(x => x !== id) : [...form.integration_ids, id] });
  };

  const addProblem = () => setForm({ ...form, problem_types: [...form.problem_types, { key: "new_problem", label: "New problem type", flow: [], identifiers: [], flow_steps: [] }] });
  const removeProblem = (idx) => setForm({ ...form, problem_types: form.problem_types.filter((_, i) => i !== idx) });
  const updateProblemAt = (idx, next) => {
    const arr = [...form.problem_types];
    arr[idx] = next;
    setForm({ ...form, problem_types: arr });
  };

  // Repos attached to squads — one per squad, with AI-generated context summary
  const [attachingRepo, setAttachingRepo] = useState(null);    // squad_id during attach
  const [refreshingRepo, setRefreshingRepo] = useState(null);  // squad_id::integ_id
  const [repoPicker, setRepoPicker] = useState({ open: null, integ_id: "" });
  const repoIntegrations = integrations.filter((i) => (i.type || "").endsWith("_repo"));

  const attachRepo = async (squadId, integrationId) => {
    if (!integrationId) return;
    setAttachingRepo(squadId);
    try {
      await api.post(`/squads/${squadId}/repos`, { integration_id: integrationId });
      toast.success(lang === "pt" ? "Repo anexado · contexto gerado pela IA" : "Repo attached · context generated by AI");
      setRepoPicker({ open: null, integ_id: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setAttachingRepo(null); }
  };

  const detachRepo = async (squadId, integrationId) => {
    if (!confirm(lang === "pt" ? "Remover este repo do squad?" : "Detach this repo from squad?")) return;
    await api.delete(`/squads/${squadId}/repos/${integrationId}`);
    load();
  };

  const refreshRepoCtx = async (squadId, integrationId) => {
    const key = `${squadId}::${integrationId}`;
    setRefreshingRepo(key);
    try {
      await api.post(`/squads/${squadId}/repos/${integrationId}/refresh-context`);
      toast.success(lang === "pt" ? "Contexto atualizado" : "Context refreshed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setRefreshingRepo(null); }
  };

  // ─── RAG indexing (Phase 7) ───────────────────────────────────────────
  const [indexStatus, setIndexStatus] = useState({}); // squadId → status payload
  const [indexing, setIndexing] = useState(null);     // squadId during dispatch
  const loadIndexStatus = async (squadId) => {
    try {
      const r = await api.get(`/squads/${squadId}/index-status`);
      setIndexStatus((s) => ({ ...s, [squadId]: r.data }));
    } catch (e) { /* silent */ }
  };
  const triggerIndex = async (squadId) => {
    setIndexing(squadId);
    try {
      await api.post(`/squads/${squadId}/index`);
      toast.success(lang === "pt" ? "Indexação disparada · acompanhe abaixo" : "Indexing dispatched · watch below");
      setTimeout(() => loadIndexStatus(squadId), 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setIndexing(null); }
  };
  const stageIcon = (status) => {
    if (status === "ok") return <CheckCircle2 className="w-3 h-3 text-green-400" />;
    if (status === "failed") return <X className="w-3 h-3 text-red-400" />;
    if (status === "skipped") return <span className="text-white/30 text-[9px] font-mono">—</span>;
    return <Loader2 className="w-3 h-3 text-white/40 animate-spin" />;
  };

  return (
    <div className="p-8 lg:p-12 max-w-6xl">
      <div className="overline mb-3">// squads · problem types · flows</div>
      <div className="flex justify-between items-end mb-2">
        <div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Squads</h1>
          <p className="text-white/50 text-sm mt-2 max-w-2xl">
            {lang === "pt"
              ? "Agrupe integrações + tipos de problema + fluxos textuais. O bridge daemon usa esses fluxos como hints para os subprocess agents."
              : "Bundle integrations + problem types + textual flows. The bridge daemon uses these flows as hints for its subprocess agents."}
          </p>
        </div>
        {!showNew && (
          <button onClick={() => setShowNew(true)} className="btn-primary text-sm py-2 px-4" data-testid="squads-new-btn">
            <Plus className="w-4 h-4" /> {lang === "pt" ? "Novo squad" : "New squad"}
          </button>
        )}
      </div>

      {showNew && (
        <form onSubmit={save} className="surface p-6 mb-8 mt-6 space-y-4" data-testid="squads-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-1">{lang === "pt" ? "Nome" : "Name"}</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Payments Squad" className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="squads-name-input" />
            </div>
            <div>
              <label className="overline block mb-1">{lang === "pt" ? "Descrição" : "Description"}</label>
              <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder={lang === "pt" ? "Cuida do gateway de pagamento" : "Handles payment gateway"}
                className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="squads-desc-input" />
            </div>
          </div>

          <div>
            <div className="overline mb-2">{lang === "pt" ? "Integrações ligadas" : "Linked integrations"} · {form.integration_ids.length}</div>
            <div className="flex flex-wrap gap-2">
              {integrations.length === 0 && <div className="text-xs text-white/40 font-mono">{lang === "pt" ? "Nenhuma integração conectada ainda" : "No integrations connected yet"}</div>}
              {integrations.map(i => {
                const sel = form.integration_ids.includes(i.id);
                return (
                  <button type="button" key={i.id} onClick={() => toggleInteg(i.id)}
                    className={`text-xs font-mono px-2 py-1.5 border transition ${sel ? "border-primary bg-primary/10 text-primary" : "border-white/10 text-white/60 hover:border-white/30"}`}
                    data-testid={`squads-integ-${i.id}`}>
                    {sel ? "✓ " : ""}{i.name} · {i.type}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="overline mb-2 flex items-center justify-between">
              <span>{lang === "pt" ? "Tipos de problema · fluxo" : "Problem types · flow"} · {form.problem_types.length}</span>
              <button type="button" onClick={addProblem} className="text-xs text-primary hover:underline" data-testid="squads-add-problem">+ {lang === "pt" ? "Adicionar" : "Add"}</button>
            </div>
            <div className="space-y-3">
              {form.problem_types.map((pt, i) => (
                <ProblemTypeEditor
                  key={i}
                  value={pt}
                  onChange={(next) => updateProblemAt(i, next)}
                  onDelete={() => removeProblem(i)}
                  lang={lang}
                  squadId={editing}
                />
              ))}
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <button disabled={saving} className="btn-primary text-sm" data-testid="squads-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {lang === "pt" ? "Salvar squad" : "Save squad"}
            </button>
            <button type="button" onClick={reset} className="btn-ghost text-sm">{lang === "pt" ? "Cancelar" : "Cancel"}</button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
        {squads.map(sq => {
          const sqRepos = sq.repos || [];
          const attachedIds = new Set(sqRepos.map(r => r.integration_id));
          const availableRepos = repoIntegrations.filter(i => !attachedIds.has(i.id));
          return (
            <div key={sq.id} className="surface p-5" data-testid={`squad-${sq.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-primary" />
                  <div className="font-display font-bold text-lg">{sq.name}</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => startEdit(sq)} className="btn-ghost text-xs py-1 px-2" data-testid={`squad-edit-${sq.id}`}><Edit className="w-3 h-3" /></button>
                  <button onClick={() => remove(sq.id)} className="btn-ghost text-xs py-1 px-2 text-red-400" data-testid={`squad-delete-${sq.id}`}><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
              <p className="text-xs text-white/50 mb-3">{sq.description || "—"}</p>
              <div className="font-mono text-[10px] text-white/40 mb-1">
                {sq.integration_ids?.length || 0} {lang === "pt" ? "integrações" : "integrations"} · {sq.problem_types?.length || 0} {lang === "pt" ? "tipos" : "problem types"} · {sqRepos.length} {lang === "pt" ? "repos" : "repos"}
              </div>
              {sq.problem_types?.slice(0, 3).map((pt, i) => (
                <div key={i} className="font-mono text-[10px] text-white/50 truncate">→ {pt.label}</div>
              ))}

              {/* Repos · cloned per squad · context_summary generated by AI */}
              <div className="mt-4 pt-3 border-t border-white/10">
                <div className="overline mb-2 flex items-center gap-1.5 justify-between">
                  <span className="flex items-center gap-1.5"><GitBranch className="w-3 h-3" /> {lang === "pt" ? "Repos do squad" : "Squad repos"}</span>
                  {sqRepos.length > 0 && (
                    <div className="flex items-center gap-2">
                      <button onClick={() => loadIndexStatus(sq.id)} className="text-[10px] text-white/40 hover:text-white">{lang === "pt" ? "status" : "status"}</button>
                      <button onClick={() => triggerIndex(sq.id)} disabled={indexing === sq.id}
                        className="text-[10px] text-primary hover:underline flex items-center gap-1"
                        data-testid={`squad-index-${sq.id}`}>
                        {indexing === sq.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Database className="w-3 h-3" />}
                        {lang === "pt" ? "Indexar repos (RAG)" : "Index repos (RAG)"}
                      </button>
                    </div>
                  )}
                </div>

                {indexStatus[sq.id] && (
                  <div className="mb-2 bg-black/30 border border-white/10 p-2 space-y-1.5" data-testid={`squad-index-status-${sq.id}`}>
                    {(indexStatus[sq.id].repos || []).map((r) => (
                      <div key={r.integration_id} className="text-[10px] font-mono">
                        <div className="flex items-center gap-2">
                          <span className="text-white/70 truncate">{r.integration_name}</span>
                          <span className="flex items-center gap-1" title="profile">{stageIcon(r.profile_status)}<span className="text-white/40">profile</span></span>
                          <span className="flex items-center gap-1" title="schema">{stageIcon(r.schema_status)}<span className="text-white/40">schema</span></span>
                          <span className="flex items-center gap-1" title="logs">{stageIcon(r.logs_status)}<span className="text-white/40">logs</span></span>
                          {r.schema_status === "skipped" && (
                            <span className="text-[9px] bg-yellow-400/10 text-yellow-300 px-1">{lang === "pt" ? "limited accuracy" : "limited accuracy"}</span>
                          )}
                          {r.drift_detected_at && (
                            <span className="text-[9px] bg-orange-400/10 text-orange-300 px-1 flex items-center gap-0.5"><AlertTriangle className="w-2.5 h-2.5" /> drift</span>
                          )}
                        </div>
                        {r.indexed_at && <div className="text-white/30 pl-1">indexed_at={r.indexed_at}{r.lang ? ` · ${r.lang}/${r.framework || "?"}` : ""}{r.files_scanned ? ` · ${r.files_scanned} files` : ""}</div>}
                      </div>
                    ))}
                  </div>
                )}

                <div className="space-y-2">
                  {sqRepos.map((r) => {
                    const refreshKey = `${sq.id}::${r.integration_id}`;
                    return (
                      <div key={r.integration_id} className="bg-white/5 p-2.5 border border-white/10" data-testid={`squad-repo-${sq.id}-${r.integration_id}`}>
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <ProviderLogo type={r.integration_type} size={20} />
                            <div className="min-w-0">
                              <div className="text-xs font-mono truncate">{r.repo_meta?.name || r.integration_name}</div>
                              {r.business_domain && <div className="text-[10px] text-primary font-mono">{r.business_domain}</div>}
                            </div>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <button onClick={() => refreshRepoCtx(sq.id, r.integration_id)} disabled={refreshingRepo === refreshKey} className="text-white/40 hover:text-white p-1" title={lang === "pt" ? "Atualizar contexto IA" : "Refresh AI context"} data-testid={`squad-repo-refresh-${r.integration_id}`}>
                              {refreshingRepo === refreshKey ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                            </button>
                            <button onClick={() => detachRepo(sq.id, r.integration_id)} className="text-red-400 hover:text-red-300 p-1" title={lang === "pt" ? "Remover" : "Detach"} data-testid={`squad-repo-detach-${r.integration_id}`}>
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        {r.purpose && <div className="text-[11px] text-white/70 mb-1 leading-snug">{r.purpose}</div>}
                        {r.context_summary && <div className="text-[10px] text-white/50 leading-snug line-clamp-2">{r.context_summary}</div>}
                        {!!r.tech?.length && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {r.tech.slice(0, 5).map((tk, i) => (
                              <span key={i} className="text-[9px] font-mono bg-primary/10 text-primary px-1.5 py-0.5">{tk}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {sqRepos.length === 0 && <div className="text-[10px] text-white/30 font-mono">{lang === "pt" ? "Nenhum repo anexado" : "No repos attached"}</div>}
                </div>

                {/* Attach picker */}
                {repoPicker.open === sq.id ? (
                  <div className="mt-2 flex gap-1.5">
                    <select value={repoPicker.integ_id} onChange={(e) => setRepoPicker({ ...repoPicker, integ_id: e.target.value })}
                      className="flex-1 bg-black/40 border border-white/10 px-2 py-1.5 text-xs font-mono" data-testid={`squad-repo-picker-${sq.id}`}>
                      <option value="">{lang === "pt" ? "selecione…" : "select…"}</option>
                      {availableRepos.map(i => (<option key={i.id} value={i.id}>{i.name} · {i.type}</option>))}
                    </select>
                    <button disabled={!repoPicker.integ_id || attachingRepo === sq.id} onClick={() => attachRepo(sq.id, repoPicker.integ_id)} className="btn-primary text-xs py-1 px-2" data-testid={`squad-repo-attach-${sq.id}`}>
                      {attachingRepo === sq.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                      {lang === "pt" ? "Anexar + analisar" : "Attach + analyze"}
                    </button>
                    <button onClick={() => setRepoPicker({ open: null, integ_id: "" })} className="text-xs text-white/40 px-1">×</button>
                  </div>
                ) : (
                  availableRepos.length > 0 && (
                    <button onClick={() => setRepoPicker({ open: sq.id, integ_id: "" })} className="text-[11px] text-primary hover:underline mt-2" data-testid={`squad-repo-add-${sq.id}`}>
                      + {lang === "pt" ? "Anexar repo" : "Attach repo"}
                    </button>
                  )
                )}
              </div>
            </div>
          );
        })}
        {squads.length === 0 && !showNew && (
          <div className="md:col-span-2 surface p-12 text-center text-white/40 text-sm">
            <Users className="w-8 h-8 mx-auto mb-3 opacity-30" />
            {lang === "pt" ? "Nenhum squad ainda. Crie um para organizar suas integrações por domínio." : "No squads yet. Create one to organize your integrations by domain."}
          </div>
        )}
      </div>
    </div>
  );
}
